import Tree, { deepClone } from "./Tree";
import api from "./../api";
import { Direction, Positions, Preferences, TreeDefaultNodeIds } from "../../utils/constants";
import { v4 as uuidv4 } from "uuid";
import { i18n } from "../../main";
import { RedZoneHelper } from "../helpers/RedZoneHelper";

let redZoneHelper = null;
export default {
  namespaced: true,
  state: {
    persistTimeout: null,
    favorites: {},
    tree: null,
    activePlayer: {
      id: null,
      editMode: false,
    },
    visibleItemIds: [],
    isLoading: false,
    playersHashTable: {}, // for fast access to player data
    defaultPlayers: {},
    showTrash: false,
    selectedTrashItems: [],
  },
  mutations: {},
  actions: {
    recalculateHashTable({ commit, state, dispatch }) {
      state.playersHashTable = state.tree.getHashTable(state.tree.allData);
    },
    addFavorite({ commit, state, dispatch }, { favoriteIndex, id, title }) {
      const favoriteExists = Object.values(state.favorites).some((item) => item.id === id);
      if (favoriteExists) return;

      state.favorites = { ...state.favorites, [favoriteIndex]: { id, title } };
      dispatch("persist");
    },
    removeFavorite({ commit, state, dispatch }, { id }) {
      const newFavorites = { ...state.favorites };
      const favoriteIndex = Object.values(state.favorites).findIndex((item) => item.id === id);
      delete newFavorites[favoriteIndex];

      state.favorites = newFavorites;
    },
    setActiveItems({ commit, state, dispatch }, id) {
      if (!id) id = TreeDefaultNodeIds.Root;

      state.tree.setActiveSubTree(id);
      dispatch("calculateVisibleItemIds");
      dispatch("setActiveItem", { id });
      state.tree.setValue(TreeDefaultNodeIds.Trash, "hide", true);
    },
    async load({ commit, state, dispatch, getters }) {
      try {
        state.isLoading = true;

        const result = await api.get(`players`);
        // Next Line For backwards compatibility
        // Can be deleted once in production
        addTypeToPlayer(result.data);
        state.tree = new Tree(result.data);

        await dispatch("calculateVisibleItemIds");
        await dispatch("recalculateHashTable");
        dispatch("smartSearch/loadPlayers", null, { root: true });
        redZoneHelper = new RedZoneHelper({ state, dispatch, getters });
        state.isLoading = false;
      } catch (error) {
        console.log(error);
        state.isLoading = false;
      }
    },
    async loadTrash({ commit, state, dispatch }) {
      dispatch("setActiveItems", TreeDefaultNodeIds.Trash);
      state.tree.setValue(TreeDefaultNodeIds.Trash, "hide", false);
    },
    toggleTrash({ commit, state, dispatch }, value) {
      if (value === undefined) value = !state.showTrash;
      state.showTrash = value;
    },
    setSelectedTrashItems({ commit, state, dispatch }, value) {
      state.selectedTrashItems = [...value];
    },
    toggleExpanded(_, { id, expanded = null }) {
      redZoneHelper.toggleExpanded({ id, expanded });
    },
    addPlayer({ commit, state, dispatch, getters }, { id }) {
      if (state.isLoading) return;
      state.isLoading = true;

      const teamId = getters["teamId"](id);
      if (
        id === TreeDefaultNodeIds.Default ||
        teamId === TreeDefaultNodeIds.Default ||
        teamId === TreeDefaultNodeIds.Trash
      ) {
        dispatch(
          "addToast",
          {
            ...i18n.global.tm("processDesigner.addPlayer.failed"),
            severity: "error",
            life: 3000,
          },
          { root: true }
        );
        return (state.isLoading = false);
      }

      const uniqueId = uuidv4();
      state.tree.addNode(
        {
          id: uniqueId,
          itemId: uniqueId,
          title: "New Player",
          expanded: true,
          type: "player",
          children: [],
        },
        id
      );
      dispatch("setActiveItem", { id: uniqueId, editMode: true });
      dispatch("calculateVisibleItemIds");
      dispatch("recalculateHashTable");
      dispatch("smartSearch/loadPlayers", null, { root: true });
      dispatch("persist");
      state.isLoading = false;
    },
    async remove({ commit, state, dispatch, getters }, { ids }) {
      redZoneHelper.remove({ ids });
    },
    async removePermanently({ state, dispatch }, { ids }) {
      redZoneHelper.removePermanently({ ids });
      dispatch("smartSearch/loadPlayers", null, { root: true });
      dispatch(
        "preferences/toggleFavorites",
        { type: Preferences.FavoritePlayers, ids, isDelete: true },
        { root: true }
      );
    },
    restore({ commit, state, dispatch }, { ids }) {
      const teamIds = [];
      ids.forEach((id) => {
        const item = state.tree.getNode(state.tree.allData, id);
        const trashTeamId = item.parents[2];
        const lastParentId = item.reference[item.reference.length - 1];
        const lastParent = state.tree.getNode(state.tree.allData, lastParentId);
        teamIds.push(item.reference[1]);
        // if original return point doesn't exist or is in trash itself then return to child of main team
        const returnPoint =
          lastParent && lastParent.parents[1] !== TreeDefaultNodeIds.Trash ? lastParentId : item.reference[1];

        state.tree.setValue(id, "reference", null);
        state.tree.moveNode(id, Positions.Center, returnPoint);

        // remove empty trash can
        const trashTeamNode = state.tree.getNode(state.tree.allData, trashTeamId);
        if (trashTeamNode?.children.length === 0) {
          state.tree.removeNode(trashTeamId);
        }
      });

      dispatch("resetActiveContextMenu", null, { root: true });
      dispatch("calculateVisibleItemIds");
      dispatch("persist", teamIds);
    },
    rename({ commit, state, dispatch, getters, rootState }, { id, title }) {
      const teamId = getters["teamId"](id);
      if (
        id === TreeDefaultNodeIds.Default ||
        teamId === TreeDefaultNodeIds.Default ||
        teamId === TreeDefaultNodeIds.Trash
      )
        return;

      state.tree.setValue(id, "title", title);
      if (rootState.cells.current !== null) {
        dispatch("cells/updateLink", { linkId: id, title }, { root: true });
      }

      // reset player getting edited
      dispatch("setActiveItem", { id, editMode: false });
      dispatch("recalculateHashTable");
      dispatch("smartSearch/loadPlayers", null, { root: true });
      dispatch("persist", teamId);
    },
    async persist({ commit, state, dispatch }, teamIds = null) {
      clearTimeout(state.persistTimeout);
      state.persistTimeout = setTimeout(async () => {
        try {
          const activeItem = state.tree.getNode(state.tree.allData, state.activePlayer.id);
          const activeTeamIds = Array.isArray(teamIds) ? teamIds : [teamIds || activeItem.parents[1]];
          const payload = [];

          activeTeamIds.forEach((activeTeamId) => {
            if (activeTeamId === TreeDefaultNodeIds.Trash || activeTeamId === TreeDefaultNodeIds.Default) return;

            let list = deepClone(state.tree.getNode(state.tree.allData, activeTeamId));
            let trashList = deepClone(state.tree.getNode(state.tree.allData, `TRASH_${activeTeamId}`) || "");

            state.tree.removeMetaData(list);
            trashList && state.tree.removeMetaData(trashList);

            payload.push({ list, trash_list: trashList, team_id: activeTeamId });
          });
          await api.patch("players", payload);
        } catch (error) {
          console.log(error);
        }
      }, 500);
    },
    setActiveItem({ commit, state, dispatch }, { id, itemId, editMode = false }) {
      if (editMode) {
        dispatch("resetActiveContextMenu", null, { root: true });
      }
      state.activePlayer = {
        id,
        itemId: itemId ?? id,
        editMode,
      };
    },
    resetActiveItem({ commit, state, dispatch }) {
      state.activePlayer = {
        id: null,
        editMode: false,
      };
    },
    // move active navigation item up and down
    moveActiveItem({ commit, state, dispatch }, direction) {
      if (!state.activePlayer.id) {
        const id = state.visibleItemIds[0];
        return dispatch("setActiveItem", { id });
      }

      let index = state.visibleItemIds.findIndex((item) => item === state.activePlayer.id);
      if (direction === Direction.Up) index = index > 0 ? index - 1 : 0;
      else index = index < state.visibleItemIds.length - 1 ? index + 1 : index;

      const newElementId = state.visibleItemIds[index];
      const element = document.querySelector(`.kk-players__tree [data-id="${newElementId}"]`);
      element.scrollIntoView({ block: "center" });

      const newActivePlayerId = state.visibleItemIds[index];
      dispatch("setActiveItem", { id: newActivePlayerId });
    },
    movePlayer({ commit, state, dispatch, getters }, { id, targetId, position }) {
      if (!getters.isMoveAllowed(id, targetId)) return;
      state.tree.moveNode(id, position, targetId);

      dispatch("calculateVisibleItemIds");
      dispatch("persist");
    },
    calculateVisibleItemIds({ commit, state, dispatch }) {
      state.visibleItemIds = state.tree.getVisibleNodeIds(state.tree.activeSubTree);
    },
    moveNode(_, { id, direction }) {
      redZoneHelper.moveNode({ id, direction });
    },
    moveNodeOneLevel(_, { id, direction }) {
      redZoneHelper.moveNodeOneLevel({ id, direction });
    },
    bringIntoView({ commit, state, dispatch }, { id }) {
      state.tree.expandParentNodes(id);
      dispatch("calculateVisibleItemIds");
    },
  },
  getters: {
    teamId: (state) => (id) => {
      const activeItem = state.tree.getNode(state.tree.allData, id);
      let teamId = activeItem?.parents[1] ?? id;
      if (TreeDefaultNodeIds.Trash === teamId) {
        teamId = activeItem.reference[1];
      }
      return teamId;
    },
    getPlayerById: (state) => (id) => {
      return state.tree?.getNode(state.tree.allData, id);
    },
    getTrashItems: (state, getters) => () => {
      const items = [];
      const trash = getters.getPlayerById(TreeDefaultNodeIds.Trash);
      trash.children.forEach((item) => {
        items.push(...item.children);
      });

      return items;
    },
    search: (state) => (searchTerm) => {
      const items = state.tree?.filter((item) => item.title.toLowerCase().includes(searchTerm.toLowerCase()));
      return items;
    },
    isMoveUpAllowed: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (
        node.level === 1 ||
        node.level === 2 ||
        node.parents[1] === TreeDefaultNodeIds.Default ||
        node.parents[1] === TreeDefaultNodeIds.Trash
      )
        return false;
      return true;
    },
    // used for move one level down and moving item up and down
    isMoveDownAllowed: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (
        node.level === 1 ||
        node.parents[1] === TreeDefaultNodeIds.Default ||
        node.parents[1] === TreeDefaultNodeIds.Trash
      )
        return false;
      return true;
    },
    // used when item is dragged and dropped
    isMoveAllowed: (state) => (id, targetId) => {
      const node = state.tree.getNode(state.tree.allData, id);
      const target = state.tree.getNode(state.tree.allData, targetId);

      if (
        node.parents[1] !== target.parents[1] ||
        node.parents[1] === TreeDefaultNodeIds.Default ||
        node.parents[1] === TreeDefaultNodeIds.Trash
      )
        return false;

      return true;
    },
  },
};

function addTypeToPlayer(player) {
  if (!player.type) player.type = "player";
  for (let i = 0; i < player.children.length; i++) addTypeToPlayer(player.children[i]);
}
